import React, { useState, useCallback, useEffect } from "react";
import { Input, Image, Button, Form, Select, message, DatePicker, InputNumber, Modal } from "antd";
import dayjs from "dayjs";
import { RcFile } from "antd/es/upload/interface";
import { ApolloError } from "@apollo/client";

import {
	Properties,
	Maybe,
	Installed_Products,
	Installed_Product_Attachments_Insert_Input
} from "graphql/generated/graphql";
import { useUpdateInstalledProductByIdMutation } from "graphql/mutations/update-installed-product-by-id.generated";
import { useAddInstalledProductAttachmentsMutation } from "graphql/mutations/add-installed-product-attachment.generated";
import { useDeleteInstalledProductAttachmentByIdMutation } from "graphql/mutations/delete-installed-attachment-by-id.generated";

import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import type { DeviceInstallation, DetectedDevice } from "hooks/useDeviceDetectionCore";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";
import LocationHierarchySelect, {
	LocationHierarchyValue,
	emptyLocationHierarchyValue
} from "components/location-hierarchy-select/location-hierarchy-select";

import { createFirebaseFileName, uploadToStorage } from "utils/files-management";

const { TextArea } = Input;

interface NewInstalledProductModalProps {
	open: boolean;
	onCancel: () => void;
	onSuccess?: () => void;
	onClickAttachment?: (file: any) => void;
	property: Maybe<Properties>;
	detectedInstallation?: DeviceInstallation & { product: DetectedDevice };
	existingInstalledProduct?: Installed_Products | null; // For editing existing installed products
	/** Callback to update the installed product in the parent's local state (device detection flow only) */
	onUpdateInstalledProduct?: (
		draft_installed_product_id: string,
		updates: Partial<DeviceInstallation>
	) => Promise<void>;
	/** Callback to add a brand-new installed product to a product in local state (device detection flow only) */
	onAddInstalledProduct?: (installation: Partial<DeviceInstallation>) => Promise<void>;
}

const NewInstalledProductModal: React.FC<NewInstalledProductModalProps> = ({
	open,
	onCancel,
	onSuccess,
	onClickAttachment,
	property,
	detectedInstallation,
	existingInstalledProduct,
	onUpdateInstalledProduct,
	onAddInstalledProduct
}) => {
	const { updateSelectedAttachments } = useSelectedAttachments();

	const [selectedProduct, setSelectedProduct] = useState<Maybe<DetectedDevice>>(null);

	const [serial, setSerial] = useState<string>("");
	const [installationDate, setInstallationDate] = useState<dayjs.Dayjs | null>(null);
	const [warrantyExpirationDate, setWarrantyExpirationDate] = useState<dayjs.Dayjs | null>(null);
	const [installationLocation, setInstallationLocation] = useState<string>("");
	const [locationHierarchy, setLocationHierarchy] = useState<LocationHierarchyValue>(emptyLocationHierarchyValue);
	const [installationNotes, setInstallationNotes] = useState<string>("");

	const [uploading, setUploading] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState<boolean>(false);
	const [attachmentsFiles, setAttachmentsFiles] = useState<Array<any>>([]);
	const [addProductPhotoModalOpen, setAddProductPhotoModalOpen] = useState<boolean>(false);
	const [installedProductPhotoThumbUrl, setInstalledProductPhotoThumbUrl] = useState<string>("");
	const [installedProductPhotoFile, setInstalledProductPhotoFile] = useState<RcFile | null>(null);

	// Determine if we're in edit mode
	const isEditMode = !!existingInstalledProduct;

	// Preload values when the modal is opened
	useEffect(() => {
		if (open) {
			if (existingInstalledProduct) {
				// Edit mode: load existing installed product
				const {
					product,
					product_serial,
					location,
					building_id,
					floor_id,
					room_id,
					product_notes,
					installation_date,
					warranty_expiration_date,
					photo_link,
					installed_product_attachments
				} = existingInstalledProduct;

				setSelectedProduct(product as any);
				setSerial(product_serial || "");
				setInstallationLocation(location || "");
				setLocationHierarchy({
					buildingId: building_id || null,
					floorId: floor_id || null,
					roomId: room_id || null
				});
				setInstallationNotes(product_notes || "");

				if (installation_date) {
					setInstallationDate(dayjs(installation_date));
				}

				if (warranty_expiration_date) {
					setWarrantyExpirationDate(dayjs(warranty_expiration_date));
				}

				if (photo_link) {
					setInstalledProductPhotoThumbUrl(photo_link);
				}

				if (installed_product_attachments && installed_product_attachments.length > 0) {
					setAttachmentsFiles(installed_product_attachments);
				}
			} else if (detectedInstallation) {
				// Create mode: load detected installation
				const {
					product,
					product_serial,
					location,
					building_id,
					floor_id,
					room_id,
					product_notes,
					installation_date,
					warranty_expiration_date,
					photo_link,
					attachments
				} = detectedInstallation;

				setSelectedProduct(product);
				setSerial(product_serial || "");
				setInstallationLocation(location || "");
				setLocationHierarchy({
					buildingId: building_id || null,
					floorId: floor_id || null,
					roomId: room_id || null
				});
				setInstallationNotes(product_notes || "");

				if (installation_date) {
					// Convert string/Date to dayjs object
					setInstallationDate(dayjs(installation_date));
				}

				if (warranty_expiration_date) {
					// Convert string/Date to dayjs object
					setWarrantyExpirationDate(dayjs(warranty_expiration_date));
				}

				if (photo_link) {
					setInstalledProductPhotoThumbUrl(photo_link);
				}

				if (attachments && attachments.length > 0) {
					setAttachmentsFiles(attachments);
				}
			}
		} else {
			// Clean when closed
			resetForm();
		}
	}, [open, existingInstalledProduct, detectedInstallation]);

	const resetForm = () => {
		setSelectedProduct(null);
		setSerial("");
		setInstallationDate(null);
		setWarrantyExpirationDate(null);
		setInstallationLocation("");
		setLocationHierarchy(emptyLocationHierarchyValue);
		setInstallationNotes("");
		setInstalledProductPhotoThumbUrl("");
		setInstalledProductPhotoFile(null);
		setAttachmentsFiles([]);
	};

	const [updateInstalledProductByIdMutation, { loading: loadingUpdate }] = useUpdateInstalledProductByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: () => {
			message.success("Installed product updated successfully");
			resetForm();
			if (onSuccess) onSuccess();
			onCancel();
		},
		onError: (error: ApolloError) => {
			console.error("updateInstalledProductByIdMutation Error:", error);
			message.error("Failed to update installed product");
		},
		refetchQueries: ["getHandoverById", "getInstalledProductById"]
	});

	const [addInstalledProductAttachmentsMutation, { loading: loadingAddAttachment }] =
		useAddInstalledProductAttachmentsMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			refetchQueries: ["getHandoverById", "getInstalledProductById"]
		});

	const [deleteInstalledProductAttachmentByIdMutation, { loading: loadingDeleteAttachment }] =
		useDeleteInstalledProductAttachmentByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			refetchQueries: ["getHandoverById", "getInstalledProductById"]
		});

	const handleOnCloseAttachmentModal = useCallback(async () => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			// Use uid for local files, or the appropriate link for DB files
			const identifier =
				attachmentFile.uid ||
				attachmentFile.photo_link ||
				attachmentFile.video_link ||
				attachmentFile.document_link ||
				attachmentFile.audio_link;

			const newList = attachmentsFiles.filter(element => {
				const elementId =
					element.uid || element.photo_link || element.video_link || element.document_link || element.audio_link;
				return elementId !== identifier;
			});
			setAttachmentsFiles(newList);
		},
		[attachmentsFiles]
	);

	// Handle removing existing attachments (for edit mode)
	const handleOnRemoveExistingAttachment = useCallback(
		(attachmentFile: any) => {
			const { id } = attachmentFile;
			if (id && isEditMode) {
				deleteInstalledProductAttachmentByIdMutation({
					variables: { id }
				});
			}
			handleOnRemoveAttachment(attachmentFile);
		},
		[isEditMode, deleteInstalledProductAttachmentByIdMutation, handleOnRemoveAttachment]
	);

	const handleOnEditAttachment = useCallback(
		(attachmentFile: any) => {
			updateSelectedAttachments(attachmentFile);
			setAddAttachmentModalEditMode(true);
			setAddAttachmentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { uid, name, file, type } = attachmentFile;

		const newAttachment = {
			uid,
			name,
			type,
			file,
			mime_type: file.type,
			originFileObj: file.originFileObj,
			localUrl: URL.createObjectURL(file.originFileObj)
		};

		setAttachmentsFiles(prev => {
			const filteredList = prev.filter(element => element.uid !== uid);
			return filteredList.concat([newAttachment]);
		});
	}, []);

	const handleOnConfirmPhoto = useCallback((file: any, url: string) => {
		setInstalledProductPhotoThumbUrl(url);
		setInstalledProductPhotoFile(file);
	}, []);

	const handleOnRemovePhoto = useCallback(async () => {
		setInstalledProductPhotoThumbUrl("");
		setInstalledProductPhotoFile(null);
	}, []);

	// Handle update for existing installed product
	const handleUpdateInstalledProduct = useCallback(async () => {
		if (!existingInstalledProduct) return;

		setUploading(true);

		try {
			// 1. Upload new photo if changed
			let photoLink = installedProductPhotoThumbUrl ? installedProductPhotoThumbUrl : null;
			if (installedProductPhotoFile) {
				const bucketName = "installed-product-photos";
				const fileName = createFirebaseFileName(installedProductPhotoFile, "installed-product-photo", serial || existingInstalledProduct.id);
				const { publicUrl } = await uploadToStorage(installedProductPhotoFile, () => {}, `${bucketName}/${fileName}`);
				photoLink = publicUrl;
			}

			// 2. Upload new attachments in parallel
			const newAttachments = attachmentsFiles.filter(att => att.uid && !att.id);
			let attachmentsToUpload: Installed_Product_Attachments_Insert_Input[] = [];

			if (newAttachments.length > 0) {
				// Map each attachment to an upload promise
				const uploadPromises = newAttachments.map(async attachment => {
					const { file, name, type } = attachment;
					const mimeType = file.type;
					const mediaType = mimeType.split("/")[0];

					// Determine bucket path based on type
					let bucketSubPath = "documents";
					let linkField: "document_link" | "photo_link" | "video_link" | "audio_link" = "document_link";

					if (type === "photo" || mediaType === "image") {
						bucketSubPath = "photos";
						linkField = "photo_link";
					} else if (type === "video" || mediaType === "video") {
						bucketSubPath = "videos";
						linkField = "video_link";
					} else if (type === "audio" || mediaType === "audio") {
						bucketSubPath = "audios";
						linkField = "audio_link";
					}

					// Upload file
					const bucketName = `installed-product-attachments/${bucketSubPath}`;
					const fileName = createFirebaseFileName(file, type || "attachment", name);
					const { publicUrl } = await uploadToStorage(file, () => {}, `${bucketName}/${fileName}`);

					// Return attachment object for database
					return {
						installed_product_id: existingInstalledProduct.id,
						name,
						type,
						mime_type: mimeType,
						[linkField]: publicUrl
					} as Installed_Product_Attachments_Insert_Input;
				});

				// Wait for all uploads to complete
				const uploadResults = await Promise.allSettled(uploadPromises);

				// Filter only successful uploads
				attachmentsToUpload = uploadResults
					.filter(result => result.status === "fulfilled")
					.map(result => (result as PromiseFulfilledResult<Installed_Product_Attachments_Insert_Input>).value);
			}

			// 3. Insert new attachments if any
			if (attachmentsToUpload.length > 0) {
				await addInstalledProductAttachmentsMutation({
					variables: { objects: attachmentsToUpload }
				});
			}

			// 4. Update installed product
			await updateInstalledProductByIdMutation({
				variables: {
					id: existingInstalledProduct.id,
					set: {
						product_serial: serial,
						installation_date: installationDate?.toISOString(),
						warranty_expiration_date: warrantyExpirationDate?.toISOString(),
						location: installationLocation,
						building_id: locationHierarchy.buildingId,
						floor_id: locationHierarchy.floorId,
						room_id: locationHierarchy.roomId,
						product_notes: installationNotes,
						photo_link: photoLink
					}
				}
			});

			setUploading(false);
		} catch (error) {
			console.error("Error updating installed product:", error);
			setUploading(false);
			message.error("Failed to update installed product");
		}
	}, [
		existingInstalledProduct,
		serial,
		installationDate,
		warrantyExpirationDate,
		installationLocation,
		locationHierarchy,
		installationNotes,
		installedProductPhotoThumbUrl,
		installedProductPhotoFile,
		attachmentsFiles,
		updateInstalledProductByIdMutation,
		addInstalledProductAttachmentsMutation
	]);

	const handleOnSubmit = useCallback(async () => {
		// if (!serial) {
		// 	message.warning("Please input the Serial Number");
		// 	return;
		// }

		if (isEditMode) {
			// Edit mode: update existing installed product
			await handleUpdateInstalledProduct();
		} else {
			// Create mode: update Zustand store
			setUploading(true);

			try {
				const input: Partial<DeviceInstallation> = {
					product_serial: serial,
					installation_date: installationDate,
					warranty_expiration_date: warrantyExpirationDate,
					location: installationLocation,
					building_id: locationHierarchy.buildingId,
					floor_id: locationHierarchy.floorId,
					room_id: locationHierarchy.roomId,
					product_notes: installationNotes,
					attachments: attachmentsFiles.length > 0 ? attachmentsFiles : undefined,
					photo_link: installedProductPhotoThumbUrl || undefined,
					installedProductPhotoFile: installedProductPhotoFile || undefined
				};

				if (onAddInstalledProduct) {
					// New installation being added manually to an existing product
					await onAddInstalledProduct(input);
				} else if (detectedInstallation?.draft_installed_product_id && onUpdateInstalledProduct) {
					// Editing a detected installation
					await onUpdateInstalledProduct(detectedInstallation.draft_installed_product_id, input);
				}

				setUploading(false);
				message.success("Installed Product successfully saved");
				resetForm();
				if (onSuccess) {
					onSuccess();
				}
				onCancel();
			} catch (error) {
				console.error("Error saving installed product:", error);
				setUploading(false);
				message.error("Failed to save installed product");
			}
		}
	}, [
		isEditMode,
		handleUpdateInstalledProduct,
		serial,
		installationDate,
		warrantyExpirationDate,
		installationLocation,
		locationHierarchy,
		installationNotes,
		attachmentsFiles,
		installedProductPhotoThumbUrl,
		installedProductPhotoFile,
		detectedInstallation?.draft_installed_product_id,
		onUpdateInstalledProduct,
		onAddInstalledProduct,
		resetForm,
		onCancel,
		onSuccess
	]);

	const isLoading = uploading || loadingUpdate || loadingAddAttachment || loadingDeleteAttachment;

	return (
		<>
			<Modal
				open={open}
				onCancel={onCancel}
				footer={null}
				width={800}
				className="device-detection-installed-product-modal"
				title={isEditMode ? "Edit Installed Device" : "New Installed Device"}
			>
				<Form className="add-product-form" style={{ width: "100%", margin: "0px" }}>
					{selectedProduct ? (
						<>
							<span className="add-product-form-title">Device Information</span>

							<div className="two-inputs-container">
								<Form.Item className="new-installed-product-modal-form-item-pair">
									<Input
										name="make"
										placeholder="Make"
										className="add-product-input-pair"
										value={selectedProduct.make || ""}
										autoComplete="off"
										disabled={true}
									/>
								</Form.Item>

								<Form.Item className="new-installed-product-modal-form-item-pair">
									<Input
										name="model"
										placeholder="Model"
										className="add-product-input-pair"
										value={selectedProduct.model || ""}
										autoComplete="off"
										disabled={true}
									/>
								</Form.Item>
							</div>

							<div className="two-inputs-container">
								<Form.Item className="new-installed-product-modal-form-item-pair">
									<Input
										name="sku"
										placeholder="SKU"
										className="add-product-input-pair"
										value={selectedProduct.sku || ""}
										autoComplete="off"
										disabled={true}
									/>
								</Form.Item>

								<Form.Item className="new-installed-product-modal-form-item-pair">
									<Select
										value={selectedProduct.type?.label}
										className="add-product-input-select"
										placeholder="Device Type"
										disabled={true}
									/>
								</Form.Item>
							</div>

							<Form.Item className="new-installed-product-modal-form-item">
								<InputNumber
									name="retailPrice"
									placeholder="Retail Price"
									className="add-product-input-pair"
									value={selectedProduct.retail_price}
									disabled={true}
								/>
							</Form.Item>

							<Form.Item className="new-installed-product-modal-form-item">
								<TextArea
									name="description"
									placeholder="Description"
									style={{ height: "148px" }}
									value={selectedProduct.description}
									autoComplete="off"
									disabled={true}
								/>
							</Form.Item>
						</>
					) : null}

					<span className="add-product-form-title">Installation Details</span>

					<div className="two-inputs-container">
						<Form.Item className="new-installed-product-modal-form-item-pair">
							<Input
								name="serial"
								placeholder="Serial Number (optional)"
								className="add-product-input-pair"
								value={serial}
								autoComplete="off"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									setSerial(event.target.value);
								}}
								allowClear
								disabled={isLoading}
							/>
						</Form.Item>

						<Form.Item className="new-installed-product-modal-form-item-pair">
							<DatePicker
								allowClear
								className="installation-date"
								format={"MM/DD/YYYY"}
								placeholder={"Installation Date (optional)"}
								value={installationDate}
								onChange={date => {
									if (date) {
										setInstallationDate(date);
									} else {
										setInstallationDate(null);
									}
								}}
								disabled={isLoading}
							/>
						</Form.Item>
					</div>

					<Form.Item className="new-installed-product-modal-form-item">
						<DatePicker
							allowClear
							className="installation-date"
							format={"MM/DD/YYYY"}
							placeholder={"Warranty Expiration Date (optional)"}
							value={warrantyExpirationDate}
							onChange={date => {
								if (date) {
									setWarrantyExpirationDate(date);
								} else {
									setWarrantyExpirationDate(null);
								}
							}}
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="new-installed-product-modal-form-item">
						<LocationHierarchySelect
							propertyId={property?.id}
							value={locationHierarchy}
							onChange={setLocationHierarchy}
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="new-installed-product-modal-form-item">
						<TextArea
							placeholder="Installation Location (optional)"
							value={installationLocation}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
								setInstallationLocation(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Form.Item className="new-installed-product-modal-form-item">
						<TextArea
							placeholder="Notes (optional)"
							style={{ height: "137px", marginBottom: "21px" }}
							value={installationNotes}
							autoComplete="off"
							onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
								setInstallationNotes(event.target.value);
							}}
							allowClear
							disabled={isLoading}
						/>
					</Form.Item>

					<Button
						className="upload-product-button"
						onClick={() => setAddProductPhotoModalOpen(true)}
						disabled={isLoading}
					>
						{installedProductPhotoThumbUrl ? "Replace Device Image" : "Upload Device Image"}
					</Button>

					{installedProductPhotoThumbUrl ? (
						<div className="organization-button-container">
							<Image width={170} src={installedProductPhotoThumbUrl} />{" "}
							<img
								alt={`remove-organization-photo-icon`}
								src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
								className="organization-photo-action-icon"
								onClick={() => {
									if (!isLoading) {
										handleOnRemovePhoto();
									}
								}}
							/>
						</div>
					) : null}

					<UploadPhotoModal
						open={addProductPhotoModalOpen}
						onCancel={() => setAddProductPhotoModalOpen(false)}
						uploadMethod={handleOnConfirmPhoto}
					/>
				</Form>

				<div className="add-product-form">
					<span className="add-product-form-title">{"Device Resources"}</span>

					<div className="add-product-documents">
						<Button
							className="upload-product-button"
							onClick={() => setAddAttachmentModalOpen(true)}
							disabled={isLoading}
						>
							Add Resources
						</Button>
					</div>

					{attachmentsFiles && attachmentsFiles.length > 0 ? (
						<div className="attachments-container ">
							{attachmentsFiles.map((attachmentFile, index) => {
								const uid = attachmentFile.uid || attachmentFile.id || index;
								const { name, file, type, photo_link, video_link, document_link } = attachmentFile;

								return (
									<AttachmentItem
										key={`product-attachment-item-${uid}`}
										name={name}
										file={file}
										type={type}
										fileUrl={photo_link || video_link || document_link}
										showEditButton
										onRemove={() => {
											if (!isLoading) {
												isEditMode && attachmentFile.id
													? handleOnRemoveExistingAttachment(attachmentFile)
													: handleOnRemoveAttachment(attachmentFile);
											}
										}}
										onEdit={() => {
											if (!isLoading) {
												handleOnEditAttachment(attachmentFile);
											}
										}}
										handleOnClick={() => {
											if (onClickAttachment) {
												onClickAttachment(attachmentFile);
											}
										}}
									/>
								);
							})}
						</div>
					) : null}
				</div>

				<div className="add-product-button-container">
					<Button
						className="admin-add-product-button"
						htmlType="submit"
						onClick={handleOnSubmit}
						disabled={isEditMode ? isLoading : isLoading || !selectedProduct || !property}
						loading={isLoading}
					>
						{isEditMode ? "Update Installed Device" : "Save Installed Device"}
					</Button>
				</div>
			</Modal>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
			/>
		</>
	);
};

export default NewInstalledProductModal;
